import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { FiatWallet } from "../entities/fiat-wallet.model";
import { WalletsBridgeService } from "@modules/+new/wallets/domain/+state/wallets-bridge.service";
import { map } from "rxjs/operators";
import { FiatWalletSummary } from "@modules/+new/wallets/domain/entities/fiat-wallet-summary.model";
import { AssetsFacade } from "@modules/+new/assets/domain/+state/assets.facade";
import { Fiat } from "@modules/+new/assets/domain/entities/fiat.model";
import { CryptoCoinWalletSummary } from "@modules/+new/wallets/domain/entities/cryptocoin-wallet-summary.model";
import { CryptoCoin } from "@modules/+new/assets/domain/entities/cryptocoin.model";
import { CryptoCoinWallet } from "@modules/+new/wallets/domain/entities/cryptocoin-wallet.model";

@Injectable({
	providedIn: "any"
})
export class WalletsFacade {
	public constructor(
		private readonly bridgeService: WalletsBridgeService,
		private readonly assetsFacade: AssetsFacade
	) {}

	// TODO implement new way to handle FiatWallets
	// - get FiatWallets from remote service (API)
	// - cache FiatWallets in stateService
	// - but first check logic & dependencies in old services / models
	public getFiatWallets$(): Observable<FiatWallet[]> {
		return this.bridgeService.getFiatWallets$();
	}

	public getFiatWalletsSummary$(): Observable<FiatWalletSummary[]> {
		return combineLatest([this.assetsFacade.getFiats$(), this.getFiatWallets$()]).pipe(
			map(([fiats, fiatWallets]) => {
				if (!this.isFiatsDataValid(fiats, fiatWallets)) {
					return [];
				}

				return fiatWallets?.reduce(
					(acc: FiatWalletSummary[], fiatWallet: FiatWallet) =>
						this.prepareFiatWalletSummary(acc, fiats, fiatWallet),
					[]
				);
			})
		);
	}

	private isFiatsDataValid(fiats: Fiat[], fiatWallets: FiatWallet[]): boolean {
		return fiats?.length > 0 && fiatWallets.length > 0;
	}

	private prepareFiatWalletSummary(
		acc: FiatWalletSummary[],
		fiats: Fiat[],
		fiatWallet: FiatWallet
	): FiatWalletSummary[] {
		const fiat = fiats?.find((fiat: Fiat) => fiat?.id === fiatWallet?.fiatId);
		if (!fiatWallet || !fiat) {
			return acc;
		}
		return [...acc, { fiatWallet, fiat }];
	}

	public getFiatWalletsSummaryByWalletGuid$(fiatWalletGuid: string): Observable<FiatWalletSummary> {
		return this.getFiatWalletsSummary$().pipe(
			map((summaries: FiatWalletSummary[]) =>
				summaries.find((summary: FiatWalletSummary) => summary.fiatWallet.guid === fiatWalletGuid)
			)
		);
	}

	// TODO implement new way to handle CryptoWallets
	// - get CryptoWallets from remote service (API)
	// - cache CryptoWallets in stateService
	// - but first check logic & dependencies in old services / models
	public getCryptoWallets$(): Observable<CryptoCoinWallet[]> {
		return this.bridgeService.getCryptoWallets$();
	}

	public getCryptoCoinWalletsSummary$(): Observable<CryptoCoinWalletSummary[]> {
		return combineLatest([this.assetsFacade.getCryptoCoins$(), this.getCryptoWallets$()]).pipe(
			map(([cryptoCoins, cryptoCoinWallets]) => {
				if (!this.isCryptoCoinsDataValid(cryptoCoins, cryptoCoinWallets)) {
					return [];
				}

				return cryptoCoins?.reduce(
					(acc: CryptoCoinWalletSummary[], cryptoCoin: CryptoCoin) =>
						this.prepareCryptoCoinWalletSummary(acc, cryptoCoin, cryptoCoinWallets),
					[]
				);
			})
		);
	}

	private isCryptoCoinsDataValid(cryptoCoins: CryptoCoin[], cryptoCoinWallets: CryptoCoinWallet[]): boolean {
		return cryptoCoins?.length > 0 && cryptoCoinWallets?.length > 0;
	}

	private prepareCryptoCoinWalletSummary(
		acc: CryptoCoinWalletSummary[],
		cryptoCoin: CryptoCoin,
		allCryptoCoinWallets: CryptoCoinWallet[]
	): CryptoCoinWalletSummary[] {
		const cryptoCoinWallets = allCryptoCoinWallets?.filter(
			(wallet: CryptoCoinWallet) => wallet?.cryptoCoinId === cryptoCoin?.id
		);
		if (!cryptoCoin || !cryptoCoinWallets || cryptoCoinWallets.length === 0) {
			return acc;
		}
		return [...acc, { cryptoCoinWallets, cryptoCoin }];
	}
}
